import {WebTracerProvider} from '@opentelemetry/sdk-trace-web';
import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http';
import {registerInstrumentations} from '@opentelemetry/instrumentation';
import {FetchInstrumentation} from '@opentelemetry/instrumentation-fetch';
import {XMLHttpRequestInstrumentation} from '@opentelemetry/instrumentation-xml-http-request';
import {UserInteractionInstrumentation} from '@opentelemetry/instrumentation-user-interaction';
import {DocumentLoadInstrumentation} from '@opentelemetry/instrumentation-document-load';
import {resourceFromAttributes} from '@opentelemetry/resources';
import {W3CTraceContextPropagator} from '@opentelemetry/core';
import {BatchSpanProcessor} from '@opentelemetry/sdk-trace-base';
import {ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION} from '@opentelemetry/semantic-conventions';
import {initializePerformanceMonitoring, initializeErrorTracking} from './performance';
import {initializeSessionTracking, addContextToSpan} from './user-context';
import {NextRouter} from 'next/router';
import {instrumentNextJsRouter} from './nextjs-router';
import {ParentBasedSampler, TraceIdRatioBasedSampler, Sampler, SamplingResult} from '@opentelemetry/sdk-trace-base';
import {SpanStatusCode} from '@opentelemetry/api';
import {notification} from '../notification';

// Export debug utilities
export * from './debug-utils';

// Helper functions to extract device information
const getDeviceType = (): string => {
  const userAgent = navigator.userAgent || '';
  if (/mobile/i.test(userAgent)) return 'mobile';
  if (/tablet|ipad/i.test(userAgent)) return 'tablet';
  return 'desktop';
};

// Get OS information
const getOSInfo = (): { name: string; version: string } => {
  const userAgent = navigator.userAgent;
  let name = 'unknown';
  let version = 'unknown';

  if (/Windows/.test(userAgent)) {
    name = 'Windows';
    const match = userAgent.match(/Windows NT ([0-9.]+)/);
    if (match) version = match[1];
  } else if (/Android/.test(userAgent)) {
    name = 'Android';
    const match = userAgent.match(/Android ([0-9.]+)/);
    if (match) version = match[1];
  } else if (/iPhone|iPad|iPod/.test(userAgent)) {
    name = 'iOS';
    const match = userAgent.match(/OS ([0-9_]+)/);
    if (match) version = match[1].replace(/_/g, '.');
  } else if (/Mac/.test(userAgent)) {
    name = 'macOS';
    const match = userAgent.match(/Mac OS X ([0-9_.]+)/);
    if (match) version = match[1].replace(/_/g, '.');
  } else if (/Linux/.test(userAgent)) {
    name = 'Linux';
  }

  return {name, version};
};

// Custom sampler that handles two different sampling rates - one for errors and one for normal spans
class DualRateSampler implements Sampler {
  private errorSampler: TraceIdRatioBasedSampler;
  private normalSampler: TraceIdRatioBasedSampler;

  constructor(normalRatio = 0.1, errorRatio = 1.0) {
    this.normalSampler = new TraceIdRatioBasedSampler(normalRatio);
    this.errorSampler = new TraceIdRatioBasedSampler(errorRatio);
  }

  shouldSample(contextOrSpanContext, traceIdOrSpanName): SamplingResult {
    // Get the attributes from context if available
    const attributes = contextOrSpanContext?.attributes || {};
    const spanName = typeof traceIdOrSpanName === 'string' ? traceIdOrSpanName : '';

    // Check if this is an error span
    const isError =
      spanName.toLowerCase().includes('error') ||
      attributes['error'] === true ||
      (attributes['http.status_code'] && attributes['http.status_code'] >= 400);

    if (isError) {
      return this.errorSampler.shouldSample(contextOrSpanContext, traceIdOrSpanName);
    }

    return this.normalSampler.shouldSample(contextOrSpanContext, traceIdOrSpanName);
  }

  toString(): string {
    return `DualRateSampler(normal=${this.normalSampler.toString()}, error=${this.errorSampler.toString()})`;
  }
}

// Router instrumentation
export function initializeRouterInstrumentation(router: NextRouter | null) {
  if (!router) return;

  // Check if route tracking is enabled via environment variable
  const enableRouteTracking = process.env.NEXT_PUBLIC_OTEL_ENABLE_ROUTE_TRACKING === 'true';

  // Return early if route tracking is disabled
  if (!enableRouteTracking) {
    console.debug('[otel-client] Route tracking disabled via configuration');
    return () => {}; // Return empty cleanup function
  }
  return instrumentNextJsRouter(router);
}

// Variable to hold provider reference for force flushing
let tracerProvider: WebTracerProvider | null = null;

/**
 * Initialize all OpenTelemetry instrumentation
 */
export function initializeTracing() {
  // Skip initialization if in server environment
  if (typeof window === 'undefined') return;

  // Get current origin domain immediately for whitelisting
  const currentOrigin = window.location.origin;
  const currentHostname = currentOrigin ? new URL(currentOrigin).hostname : '';

  const initWithZone = async () => {
    try {
      const {ZoneContextManager} = await import('@opentelemetry/context-zone');

      console.debug('[otel-client] Initializing tracing...');
      try {
        // Get feature flags from environment - use constants for default values
        const DEFAULT_ENABLED = 'true';
        const DEFAULT_DISABLED = 'false';

        // Feature flags with their defaults
        const enableDocumentLoad = process.env.NEXT_PUBLIC_OTEL_ENABLE_DOCUMENT_LOAD === DEFAULT_ENABLED;
        const enableUserInteraction = process.env.NEXT_PUBLIC_OTEL_ENABLE_USER_INTERACTION === DEFAULT_ENABLED;
        const enableResourceTracking = process.env.NEXT_PUBLIC_OTEL_ENABLE_RESOURCE_TRACKING === DEFAULT_ENABLED;
        const enablePerformanceTracking = process.env.NEXT_PUBLIC_OTEL_ENABLE_PERFORMANCE === DEFAULT_ENABLED;
        const enableErrorTracking = process.env.NEXT_PUBLIC_OTEL_ENABLE_ERROR_TRACKING !== DEFAULT_DISABLED;
        const enableApiRequests = process.env.NEXT_PUBLIC_OTEL_ENABLE_API_REQUESTS !== DEFAULT_DISABLED;
        const enableRouteTracking = process.env.NEXT_PUBLIC_OTEL_ENABLE_ROUTE_TRACKING === DEFAULT_ENABLED;

        console.debug('[otel-client] Feature flags:', {
          enableDocumentLoad,
          enableUserInteraction,
          enableResourceTracking,
          enablePerformanceTracking,
          enableErrorTracking,
          enableApiRequests,
          enableRouteTracking,
        });

        // Service identification
        const serviceName = process.env.NEXT_PUBLIC_OTEL_ATTR_SERVICE_NAME || 'customer-frontend';
        const serviceVersion = process.env.NEXT_PUBLIC_OTEL_ATTR_SERVICE_VERSION || '1.0.0';

        const resource = resourceFromAttributes({
          [ATTR_SERVICE_NAME]: serviceName,
          [ATTR_SERVICE_VERSION]: serviceVersion,
        });
        console.debug('[otel-client] Resource defined:', resource.attributes);

        // Get the collector URL - required for telemetry
        const collectorUrl = process.env.NEXT_PUBLIC_OTEL_COLLECTOR_URL;

        // Exit early if collector URL is not configured
        if (!collectorUrl) {
          console.warn('[otel-client] OpenTelemetry collector URL not configured. Skipping initialization.');
          return;
        }

        // Determine exporter configuration
        const useApiProxy = process.env.NEXT_PUBLIC_OTEL_USE_API_PROXY === DEFAULT_ENABLED;
        const exporterUrl = useApiProxy ?
          `${window.location.origin}/api/otel` :
          (collectorUrl.endsWith('/v1/traces') ? collectorUrl : `${collectorUrl}/v1/traces`);

        // Export trace data to the collector
        const exporter = new OTLPTraceExporter({
          url: exporterUrl,
          headers: {'Content-Type': 'application/json'},
        });
        console.debug('[otel-client] Exporter defined to URL:', exporterUrl);

        // Span processor with batch settings
        const spanProcessor = new BatchSpanProcessor(exporter, {
          maxQueueSize: 100,
          maxExportBatchSize: 50,
          scheduledDelayMillis: 5000,
          exportTimeoutMillis: 30000,
        });
        console.debug('[otel-client] Span processor created with optimized batch settings.');

        // Get sampling ratios from environment variables
        const normalSamplingRatio = parseFloat(
          process.env.NEXT_PUBLIC_OTEL_NORMAL_SAMPLING_RATIO ||
          process.env.OTEL_NORMAL_SAMPLING_RATIO ||
          '1.0',
        );
        const errorSamplingRatio = parseFloat(
          process.env.NEXT_PUBLIC_OTEL_ERROR_SAMPLING_RATIO ||
          process.env.OTEL_ERROR_SAMPLING_RATIO ||
          '1.0',
        );

        console.debug(`[otel-client] Sampling rates - Normal: ${normalSamplingRatio}, Error: ${errorSamplingRatio}`);

        // Create custom sampler with dual rates
        const sampler = new ParentBasedSampler({
          root: new DualRateSampler(normalSamplingRatio, errorSamplingRatio),
        });

        // Initialize provider with all configurations
        const provider = new WebTracerProvider({
          resource,
          spanProcessors: [spanProcessor],
          sampler,
        });
        console.debug('[otel-client] Provider created with processor and sampler.');

        // Store provider reference for force flush capability
        tracerProvider = provider;

        // Register the provider
        provider.register({
          contextManager: new ZoneContextManager(),
          propagator: new W3CTraceContextPropagator(),
        });
        console.debug('[otel-client] Provider registered.');

        // Cache device and environment information - computed once for efficiency
        const deviceType = getDeviceType();
        const osInfo = getOSInfo();
        const environment = process.env.NEXT_PUBLIC_APP_ENV || 'unknown';
        const frameWorkInfo = {
          name: 'Next.js/React',
          version: process.env.NEXT_PUBLIC_APP_VERSION || 'unknown',
        };

        // Set up page unload handling to flush spans when user leaves the page
        const handleBeforeUnload = () => {
          if (!tracerProvider) return;

          try {
            // Best-effort flush with minimal logging during unload
            tracerProvider.forceFlush().catch(() => {/* Silent catch during unload */});
          } catch {
            // Silence errors during page unload - nothing we can do
          }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Common function to add attributes to spans - avoid duplication
        const addCommonAttributes = (span, url) => {
          // Group related attributes for clarity and performance

          // HTTP attributes
          span.setAttribute('app.synthetic_request', false);
          span.setAttribute('http.url', url.toString());
          span.setAttribute('http.path', url.pathname);
          span.setAttribute('http.target', url.pathname + url.search);
          span.setAttribute('http.host', url.hostname);
          span.setAttribute('http.scheme', url.protocol.replace(':', ''));
          span.setAttribute('http.user_agent', navigator.userAgent);

          // Page context
          span.setAttribute('page.url', window.location.href);
          span.setAttribute('page.referrer', document.referrer);
          span.setAttribute('environment', environment);
          span.setAttribute('framework.name', frameWorkInfo.name);
          span.setAttribute('framework.version', frameWorkInfo.version);

          // Device info
          span.setAttribute('device.type', deviceType);
          span.setAttribute('device.os.name', osInfo.name);
          span.setAttribute('device.os.version', osInfo.version);

          // Add user and session context
          addContextToSpan(span);
        };

        // URL to ignore for tracing - organized by category
        const ignoreTraceUrls = [
          // Category 1: Telemetry endpoints - avoid infinite loops
          /\/api\/otel/, // Ignore API proxy
          /\/v1\/traces/, // Ignore direct collector endpoint
          /\/api\/traces/, // Ignore direct collector endpoint
          new RegExp(collectorUrl.replace(/\//g, '\\/')), // Ignore the specific collector URL

          // Category 2: Next.js framework resources - make these more specific to ensure they match
          /\/_next\//i, // Ignore ALL Next.js resources with case insensitivity
          /\/_next$/i, // Also match the exact _next path
          /\/__nextjs_original-stack-frame/, // Ignore Next.js internal error frames

          // Category 3: Development resources
          /\.hot-update\.json$/, // Ignore webpack hot updates
          /\.(js|css)\.map$/, // Ignore all source maps (combined pattern)

          // Category 4: External services
          /\/cdn-run/, // Ignore CDN run URLs
        ];

        // Process domains configuration
        const tracedDomainsStr = process.env.NEXT_PUBLIC_OTEL_TRACED_DOMAINS || '';
        const excludedDomainsStr = process.env.NEXT_PUBLIC_OTEL_EXCLUDED_DOMAINS || '';

        // Parse whitelisted domains
        const tracedDomains = tracedDomainsStr ?
          tracedDomainsStr.split(',').map(domain => domain.trim()).filter(Boolean) :
          [];

        // Add current hostname if not already included
        if (currentHostname && !tracedDomains.some(domain =>
          domain === currentHostname ||
            (domain.startsWith('*.') && currentHostname.endsWith(domain.substring(2))))) {
          console.debug(`[otel-client] Adding current hostname to traced domains: ${currentHostname}`);
          tracedDomains.push(currentHostname);
        }

        // Parse excluded domains
        const excludedDomains = excludedDomainsStr ?
          excludedDomainsStr.split(',').map(domain => domain.trim()).filter(Boolean) :
          [];

        // Log possible configuration issues
        if (tracedDomains.length === 0) {
          console.warn('[otel-client] No domains configured for tracing. Only the current origin will be traced.');
        }

        // Use consistent pattern for CORS header propagation
        const propagationPatterns = [/.*/];

        // Convert domain lists to regex patterns - done once for efficiency
        const tracedDomainPatterns = tracedDomains.map(domain => {
          const pattern = domain.replace(/\./g, '\\.').replace(/\*/g, '.*');
          return new RegExp(`^https?://(.*\\.)?${pattern}($|/.*)`);
        });

        // Create patterns for excluded domains
        const exclusionPatterns = excludedDomains.map(domain => {
          if (domain.startsWith('*.')) {
            const baseDomain = domain.substring(2).replace(/\./g, '\\.');
            return new RegExp(`^https?://[^/]*\\.${baseDomain}(/.*)?$`);
          } else {
            return new RegExp(`^https?://${domain.replace(/\./g, '\\.')}(/.*)?$`);
          }
        });

        // Create negative pattern for non-whitelisted domains - UPDATE THIS SECTION
        // The previous implementation was too strict and wasn't correctly handling subdomains
        const tracedDomainsRegexParts = tracedDomains.map(domain => {
          if (domain.startsWith('*.')) {
            // For wildcard domains like *.searchatlas.com, match any subdomain
            const baseDomain = domain.substring(2).replace(/\./g, '\\.');
            return `[^/]*\\.${baseDomain}`;
          } else {
            // For exact domains, match exactly
            return domain.replace(/\./g, '\\.');
          }
        });

        // Only create this pattern if we have domains to trace
        const nonTracedDomainPatterns = tracedDomainsRegexParts.length > 0 ?
          [new RegExp(`^https?://(?!(${tracedDomainsRegexParts.join('|')})).*$`)] :
          [/.*/]; // Default to rejecting everything if no domains configured

        // Process additional ignored endpoints from env vars
        const additionalIgnoreEndpoints = process.env.NEXT_PUBLIC_OTEL_IGNORE_ENDPOINTS || '';
        if (additionalIgnoreEndpoints) {
          additionalIgnoreEndpoints.split(',')
            .map(endpoint => endpoint.trim())
            .filter(Boolean)
            .forEach(endpoint => {
              // Escape special regex chars
              const escapedEndpoint = endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              ignoreTraceUrls.push(new RegExp(escapedEndpoint));
            });
        }

        // Log the filtering configuration
        console.debug('[otel-client] URL filtering configured with:', {
          tracedDomains,
          currentHostname,
          excludedDomains,
          ignoredPatterns: ignoreTraceUrls.map(regex => regex.toString()),
        });

        // Create URL filter function to determine if a URL should be traced
        const shouldTraceUrl = (urlString: string): boolean => {
          try {
            // FAST PATH: Early rejection for common patterns we never want to trace
            // Check NextJS resources first as they are the most common to filter
            if (ignoreTraceUrls.some(pattern => pattern.test(urlString))) {
              if (process.env.NODE_ENV === 'development') {
                console.debug(`[otel-filter] URL ignored by pattern: ${urlString}`);
              }
              return false;
            }

            // PRIORITY #1: Check explicit exclusions
            if (exclusionPatterns.some(pattern => pattern.test(urlString))) {
              if (process.env.NODE_ENV === 'development') {
                console.debug(`[otel-filter] URL excluded by domain pattern: ${urlString}`);
              }
              return false;
            }

            // Continue with the rest of the logic...
            // If no domains configured, only allow current hostname
            if (tracedDomains.length === 0 && !currentHostname) {
              return false;
            }

            try {
              // Special case for current origin - always allow
              const tryUrl = new URL(urlString);
              if (tryUrl.hostname === currentHostname) {
                return true;
              }

              // Handle localhost/127.0.0.1
              const hostname = tryUrl.hostname;
              if ((hostname === 'localhost' || hostname === '127.0.0.1') &&
                 !tracedDomains.includes(hostname)) {
                return false;
              }

              // Check against traced domains
              const domainAllowed = tracedDomains.some(domain => {
                if (domain.startsWith('*.')) {
                  return hostname.endsWith(domain.substring(2));
                } else {
                  return hostname === domain;
                }
              });

              if (domainAllowed) {
                return true;
              }

              // Final check against traced patterns
              if (tracedDomainPatterns.some(pattern => pattern.test(urlString))) {
                return true;
              }

              // Not in whitelist
              return false;
            } catch (urlError) {
              // URL parsing failed, fall back to regex checks
              return tracedDomainPatterns.some(pattern => pattern.test(urlString));
            }
          } catch (err) {
            // Any error in filtering leads to rejection for security
            return false;
          }
        };

        // Register all necessary instrumentations
        const instrumentations = [];

        // Add HTTP instrumentations if API requests are enabled
        if (enableApiRequests) {
          // Common ignoreUrls configuration - combine exclusions and non-traced patterns
          // Make sure all ignoreTraceUrls are included directly
          const httpIgnoreUrls = [
            ...ignoreTraceUrls,
            ...exclusionPatterns,
            ...nonTracedDomainPatterns,
          ];

          // Custom function to create XMLHttpRequest instrumentation instance
          const createXhrInstrumentation = () => {
            // Create the underlying instrumentation instance
            const xhrInst = new XMLHttpRequestInstrumentation({
              propagateTraceHeaderCorsUrls: propagationPatterns,
              ignoreUrls: httpIgnoreUrls,
              applyCustomAttributesOnSpan: (span, xhr) => {
                const xhrAny = xhr as any;
                const internalState = (xhrInst as any)._xhrMem?.get(xhr);

                // Determine URL string for span name early (prioritize OTel internal state and openArgs)
                let urlStringForSpanName = internalState?.spanUrl || xhrAny._openArgs?.[1] || xhr.responseURL || xhrAny.__zone_symbol__xhrURL || xhrAny.extraInfo?.url || '';

                // --- Start of refined logic for span name ---
                // For Axios requests, try to extract method from several places
                // Determine method with enhanced detection for Axios
                let finalMethod = null;

                // 1. Try axios-specific properties first (if they exist)
                // Axios might store config in __axios property of onreadystatechange
                if (xhrAny.onreadystatechange &&
                    xhrAny.onreadystatechange.__axios &&
                    xhrAny.onreadystatechange.__axios.config &&
                    xhrAny.onreadystatechange.__axios.config.method) {
                  finalMethod = xhrAny.onreadystatechange.__axios.config.method;
                }

                // 2. Look in standard places if not found in axios config
                if (!finalMethod) {
                  if (internalState?.method) {
                    finalMethod = internalState.method;
                  } else if (xhrAny._openArgs?.[0]) {
                    finalMethod = xhrAny._openArgs[0];
                  } else if (xhrAny._method) {
                    finalMethod = xhrAny._method;
                  } else if (xhrAny.__zone_symbol__xhrMethod) {
                    finalMethod = xhrAny.__zone_symbol__xhrMethod;
                  } else if (xhrAny.extraInfo?.method) {
                    finalMethod = xhrAny.extraInfo.method;
                  }
                }

                // 3. If still not found, try to look deeper into the XHR object for any method-like properties
                if (!finalMethod) {
                  // Try to find method-like properties by iterating object keys
                  for (const key of Object.keys(xhrAny)) {
                    // Check for properties that might contain 'method' in the name
                    if (key.toLowerCase().includes('method') && typeof xhrAny[key] === 'string') {
                      finalMethod = xhrAny[key];
                      break;
                    }

                    // Check for Zone.js task properties which might contain method info
                    if (key === '__zone_symbol__xhrTask' && typeof xhrAny[key] === 'object' && xhrAny[key]) {
                      try {
                        // Zone.js task object may have data property with method info
                        const task = xhrAny[key];

                        // Check various spots where the method might be
                        if (task.data && task.data.args && task.data.args.length > 0) {
                          finalMethod = task.data.args[0];
                          break;
                        } else if (task.data && task.data.method) {
                          finalMethod = task.data.method;
                          break;
                        }
                      } catch (e) {
                        // Ignore errors examining Zone.js task
                      }
                    }

                    // Also check for axios-like properties
                    if (key.includes('axios') || key.includes('config')) {
                      // If it's an object, look for method property
                      if (typeof xhrAny[key] === 'object' && xhrAny[key]?.method) {
                        finalMethod = xhrAny[key].method;
                        break;
                      }
                    }
                  }
                }

                // 4. Try to infer method from URL patterns if still not found or is XHR
                if (!finalMethod || finalMethod === 'XHR') {
                  let parsedUrl: URL | null = null;
                  try {
                    if (urlStringForSpanName) {
                      parsedUrl = new URL(urlStringForSpanName);

                      // First, check for query parameters - very likely a GET request
                      if (parsedUrl.search && parsedUrl.search.length > 1) {
                        finalMethod = 'GET';
                      } else {
                        // Extract API endpoint name from path
                        const pathParts = parsedUrl.pathname.split('/').filter(Boolean);
                        const lastPathPart = pathParts[pathParts.length - 1];

                        // Heuristics for common API patterns
                        if (lastPathPart) {
                          const lowerLastPart = lastPathPart.toLowerCase();
                          if (['add', 'create', 'update', 'save'].some(action => lowerLastPart.includes(action))) {
                            finalMethod = 'POST';
                          } else if (['delete', 'remove'].some(action => lowerLastPart.includes(action))) {
                            finalMethod = 'DELETE';
                          } else if (['search', 'find', 'get', 'list'].some(action => lowerLastPart.includes(action))) {
                            finalMethod = 'GET';
                          }
                        }
                      }
                    }
                  } catch (e) {
                    // Ignore URL parsing errors
                  }
                }

                // 5. For GET requests, add extra checks since they're most common
                // If we still don't have a method and it's not a POST/DELETE/etc request body,
                // it's very likely a GET
                if ((!finalMethod || finalMethod === 'XHR') &&
                    urlStringForSpanName &&
                    urlStringForSpanName.includes('?')) {
                  finalMethod = 'GET';
                }

                // If we still don't have a method at this point, default to 'XHR'
                if (!finalMethod) {
                  finalMethod = 'XHR';
                }

                finalMethod = finalMethod.toUpperCase();

                // Process URL path for span name
                let pathForSpanName = '/unknown-path';
                if (urlStringForSpanName) {
                  try {
                    const parsedForPath = new URL(urlStringForSpanName);
                    pathForSpanName = parsedForPath.pathname;

                    // Extract subdomain for span name
                    let subdomain = 'APP';
                    const hostname = parsedForPath.hostname;

                    // Handle localhost specifically
                    if (hostname === 'localhost' || hostname === '127.0.0.1') {
                      subdomain = 'LOCAL';
                    } else {
                      // Extract subdomain from hostname
                      const hostParts = hostname.split('.');
                      if (hostParts.length > 2) {
                        // First part is the subdomain
                        subdomain = hostParts[0].toUpperCase();
                      } else if (hostParts.length === 2) {
                        // No subdomain, use first part of domain
                        subdomain = hostParts[0].toUpperCase();
                      }
                    }

                    // Update span name with subdomain
                    span.updateName(`[${subdomain}] ${pathForSpanName}`);
                  } catch (e) {
                    const pathMatch = urlStringForSpanName.match(/https?:?\/\/[^/]+(\/[^?#]*)/);
                    if (pathMatch && pathMatch[1]) {
                      pathForSpanName = pathMatch[1];
                    } else {
                      pathForSpanName = urlStringForSpanName;
                    }

                    // Default name if URL parsing fails
                    span.updateName(`[API] ${pathForSpanName}`);
                  }
                } else {
                  urlStringForSpanName = 'unknown-url';
                  // Default name for unknown URLs
                  span.updateName(`[API] /unknown-path`);
                }
                // --- End of refined logic for span name ---

                try {
                  // Access internal state to get method and URL info if possible
                  // The method for attributes can also use finalMethod for consistency
                  const methodForAttributes = finalMethod; // Use the already determined method

                  // Get URL information
                  // Use xhr.responseURL if available and valid for attributes/filtering, otherwise fallback.
                  let attributeUrlString = xhr.responseURL || urlStringForSpanName;
                  let attributeUrl: URL | undefined;

                  if (attributeUrlString) {
                    try {
                      attributeUrl = new URL(attributeUrlString);
                    } catch (e) {
                      // xhr.responseURL was invalid or empty, fallback to the URL used for span name
                      attributeUrlString = urlStringForSpanName;
                      if (urlStringForSpanName && urlStringForSpanName !== 'unknown-url') {
                        try {
                          attributeUrl = new URL(urlStringForSpanName);
                        } catch (eNested) {/* ignore nested error, attributeUrl remains undefined */}
                      }
                    }
                  } else {
                    // xhr.responseURL was not available, use URL for span name
                    attributeUrlString = urlStringForSpanName;
                    if (urlStringForSpanName && urlStringForSpanName !== 'unknown-url') {
                      try {
                        attributeUrl = new URL(urlStringForSpanName);
                      } catch (e) {/* attributeUrl remains undefined */}
                    }
                  }
                  if (!attributeUrlString) attributeUrlString = 'unknown-url';

                  // Check if this is a Next.js resource (fast path check)
                  // Use attributeUrl.pathname if available, otherwise pathForSpanName
                  // const pathToFilter = attributeUrl ? attributeUrl.pathname : pathForSpanName;
                  if (attributeUrlString.includes('/_next/') ||
                      attributeUrlString.includes('.hot-update.json') ||
                      attributeUrlString.includes('.map')) {
                    span.setAttribute('telemetry.filtered', true);
                    span.setAttribute('telemetry.filtered_reason', 'next_static_resource');
                    return;
                  }

                  // Check if URL should be traced
                  if (!shouldTraceUrl(attributeUrlString)) {
                    span.setAttribute('telemetry.filtered', true);
                    if (exclusionPatterns.some(pattern => pattern.test(attributeUrlString))) {
                      span.setAttribute('telemetry.filtered_reason', 'domain_excluded');
                    } else if (ignoreTraceUrls.some(pattern => pattern.test(attributeUrlString))) {
                      span.setAttribute('telemetry.filtered_reason', 'endpoint_ignored');
                    } else {
                      span.setAttribute('telemetry.filtered_reason', 'domain_not_traced');
                    }
                    return;
                  }

                  // Add common attributes
                  if (attributeUrl) {
                    addCommonAttributes(span, attributeUrl);
                  } else {
                    span.setAttribute('http.url', attributeUrlString); // Set raw string if URL object failed
                    // Consider adding other critical common attributes manually if needed
                  }

                  // Set HTTP method attribute explicitly
                  span.setAttribute('http.method', methodForAttributes); // Use the consistently determined method

                  // Set HTTP status code if available
                  if (xhr.readyState === 4) {
                    span.setAttribute('http.status_code', xhr.status);

                    // Set status based on HTTP status code
                    if (xhr.status >= 200 && xhr.status < 400) {
                      span.setStatus({code: SpanStatusCode.OK});
                    } else {
                      span.setStatus({
                        code: SpanStatusCode.ERROR,
                        message: `HTTP ${xhr.status}`,
                      });
                      if (xhr.status !== 0) {
                        notification.error(`Error`, `HTTP ${xhr.status}`, false, 'Refresh page', xhr.status, xhr.responseURL, true, false, false, null, null, null, null, span.spanContext()?.traceId);
                      }
                    }
                  } else {
                    // Pre-set status to OK - will be updated if error occurs
                    span.setStatus({code: SpanStatusCode.OK});
                  }
                } catch (err) {
                  // If URL parsing fails, continue but note the error
                  // eslint-disable-next-line no-console
                  console.debug('[otel-client] Could not process XHR span:', err);

                  // Error handling code - try to extract information even when URL construction fails
                  try {
                    // Try to get method first - important for span naming (already handled by finalMethod)
                    const methodInCatch = finalMethod; // Use pre-determined method

                    // Try to get URL from different sources (already handled by urlStringForSpanName)
                    const urlStringInCatch = urlStringForSpanName;

                    // Check if this is a Next.js resource even in error case
                    if (urlStringInCatch !== 'unknown-url' &&
                        (urlStringInCatch.includes('/_next/') ||
                         urlStringInCatch.includes('.hot-update.json') ||
                         urlStringInCatch.includes('.map'))) {
                      span.setAttribute('telemetry.filtered', true);
                      span.setAttribute('telemetry.filtered_reason', 'next_static_resource');
                      return;
                    }

                    // Check if the URL should be traced
                    if (urlStringInCatch !== 'unknown-url' && !shouldTraceUrl(urlStringInCatch)) {
                      // If shouldn't be traced, mark the span accordingly
                      span.setAttribute('telemetry.filtered', true);

                      // Provide reason if available
                      if (exclusionPatterns.some(pattern => pattern.test(urlStringInCatch))) {
                        span.setAttribute('telemetry.filtered_reason', 'domain_excluded');
                      } else if (ignoreTraceUrls.some(pattern => pattern.test(urlStringInCatch))) {
                        span.setAttribute('telemetry.filtered_reason', 'endpoint_ignored');
                      } else {
                        span.setAttribute('telemetry.filtered_reason', 'domain_not_traced');
                      }
                      return;
                    }

                    // Set URL attributes directly
                    span.setAttribute('http.url', urlStringInCatch);
                    span.setAttribute('http.method', methodInCatch); // Use consistent method

                    // If we have an error with the URL, set a more specific attribute for debugging
                    span.setAttribute('error.url_related', true);

                    // Try to extract domain from URL string to set host attribute
                    try {
                      const hostMatch = urlStringInCatch.match(/https?:\/\/([^/]+)/);
                      if (hostMatch && hostMatch[1]) {
                        span.setAttribute('http.host', hostMatch[1]);
                      }
                    } catch {
                      // Ignore host extraction errors
                    }

                    // Determine a better error message based on the XHR status
                    let errorMessage = 'Network error';

                    // Check if XHR has specific error information
                    if (xhr.status === 0) {
                      // Status 0 typically means network error or CORS issue
                      errorMessage = 'Network error or CORS issue';
                    } else if (xhr.statusText) {
                      errorMessage = xhr.statusText;
                    }

                    // Set error status with better message
                    span.setStatus({
                      code: SpanStatusCode.ERROR,
                      message: errorMessage,
                    });

                    // Mark the error as handled by OTEL instrumentation to prevent duplicate notifications
                    // This is crucial for preventing double error handling with axios interceptors
                    if (err && typeof err === 'object') {
                      // Add a property that our axios error handlers can check
                      (err as any).isNotificationHandled = true;

                      // Log that we've marked this error
                      if (process.env.NODE_ENV === 'development') {
                        console.debug('[otel-client] Marked error as handled by instrumentation:', errorMessage);
                      }
                    }
                  } catch (nameErr) {
                    // eslint-disable-next-line no-console
                    console.warn('[otel-client] Failed to set span name or attributes in error handler:', nameErr);

                    // Fallback status if not already set by primary error
                    if (!checkSpanStatusSet(span)) {
                      span.setStatus({
                        code: SpanStatusCode.ERROR,
                        message: String(err),
                      });
                    }
                  }

                  // Record error event
                  try {
                    span.addEvent('xhr.error', {
                      'error.type': (err as Error).name || 'Error',
                      'error.message': (err as Error).message || String(err),
                    });
                  } catch (eventErr) {
                    // eslint-disable-next-line no-console
                    console.debug('[otel-client] Failed to record error event:', eventErr);
                  }
                }
              },
            });

            // Return the instrumentation instance
            return xhrInst;
          };

          // Create XHR instrumentation with our custom factory
          const xhrInstrumentation = createXhrInstrumentation();

          // Add both Fetch and XHR instrumentations
          instrumentations.push(
            new FetchInstrumentation({
              propagateTraceHeaderCorsUrls: propagationPatterns,
              ignoreUrls: httpIgnoreUrls,
              applyCustomAttributesOnSpan: (span, request, response) => {
                let url: URL;
                let method = 'FETCH'; // Default to FETCH instead of GET to avoid confusion

                try {
                  if (typeof request === 'string') {
                    url = new URL(request);
                    // For string requests, GET is the correct default per fetch API spec
                    method = 'GET';
                  } else if ('url' in request) {
                    url = new URL(request.url);
                    // Use specified method or keep FETCH to distinguish from XHR
                    method = request.method ? request.method.toUpperCase() : method;
                  }

                  // Check if this is a Next.js resource (fast path check)
                  const urlString = url.toString();
                  if (urlString.includes('/_next/') ||
                      urlString.includes('.hot-update.json') ||
                      urlString.includes('.map')) {
                    span.setAttribute('telemetry.filtered', true);
                    span.setAttribute('telemetry.filtered_reason', 'next_static_resource');
                    return;
                  }

                  // Important: Update the span name EARLY - before any filtering
                  // This ensures the HTTP method appears in the root span name
                  try {
                    // Extract subdomain for span name
                    let subdomain = 'APP';
                    const hostname = url.hostname;

                    // Handle localhost specifically
                    if (hostname === 'localhost' || hostname === '127.0.0.1') {
                      subdomain = 'LOCAL';
                    } else {
                      // Extract subdomain from hostname
                      const hostParts = hostname.split('.');
                      if (hostParts.length > 2) {
                        // First part is the subdomain
                        subdomain = hostParts[0].toUpperCase();
                      } else if (hostParts.length === 2) {
                        // No subdomain, use first part of domain
                        subdomain = hostParts[0].toUpperCase();
                      }
                    }

                    // Update span name with subdomain
                    span.updateName(`[${subdomain}] ${url.pathname}`);
                  } catch (e) {
                    // Fallback if parsing fails
                    span.updateName(`[API] ${url.pathname}`);
                  }

                  // Check if the URL should be traced
                  if (!shouldTraceUrl(urlString)) {
                    // If shouldn't be traced, mark the span accordingly
                    span.setAttribute('telemetry.filtered', true);

                    // Provide reason if available
                    if (exclusionPatterns.some(pattern => pattern.test(urlString))) {
                      span.setAttribute('telemetry.filtered_reason', 'domain_excluded');
                    } else if (ignoreTraceUrls.some(pattern => pattern.test(urlString))) {
                      span.setAttribute('telemetry.filtered_reason', 'endpoint_ignored');
                    } else {
                      span.setAttribute('telemetry.filtered_reason', 'domain_not_traced');
                    }

                    return;
                  }

                  // Add attributes and update span name
                  addCommonAttributes(span, url);
                  // span.updateName(`${method} ${url.pathname}`); - Already called above

                  // Set up a handler to update status based on response
                  span.setAttribute('http.method', method);

                  // Pre-set status to OK - will be updated if error occurs
                  span.setStatus({code: SpanStatusCode.OK});

                  // Here we want to add error handling logic to mark errors
                  // This code should run when response is in error state
                  // @ts-expect-error
                  if (response && !response.ok) {
                    try {
                      // Add status code and status text to the span
                      span.setAttribute('http.status_code', response.status);
                      // @ts-expect-error
                      span.setAttribute('http.status_text', response.statusText);

                      // Set error status
                      span.setStatus({
                        code: SpanStatusCode.ERROR,
                        // @ts-expect-error
                        message: `HTTP ${response.status}: ${response.statusText}`,
                      });

                      // Mark this error as handled to prevent duplicate notifications
                      // We need to do this carefully since we don't have direct access to the error object
                      // This will be picked up in the caught promise rejection in fetch code
                      if (!window.__fetchErrorsHandled) {
                        window.__fetchErrorsHandled = {};
                      }

                      // Create a key from the request and response details
                      const errorKey = `${request instanceof Request ? request.url : request}:${response.status}:${Date.now()}`;
                      window.__fetchErrorsHandled[errorKey] = {
                        timestamp: Date.now(),
                        url: request instanceof Request ? request.url : String(request),
                        status: response.status,
                        // @ts-expect-error
                        statusText: response.statusText,
                      };

                      // Log that we've marked this error
                      if (process.env.NODE_ENV === 'development') {
                        // @ts-expect-error
                        console.debug('[otel-client] Marked fetch error as handled by instrumentation:', response.status, response.statusText);
                      }

                      // Clean up old entries after a short period
                      setTimeout(() => {
                        if (window.__fetchErrorsHandled && window.__fetchErrorsHandled[errorKey]) {
                          delete window.__fetchErrorsHandled[errorKey];
                        }
                      }, 5000);
                    } catch (err) {
                      console.warn('[otel-client] Error handling fetch response:', err);
                    }
                  }
                } catch (err) {
                  // If URL parsing fails, continue but note the error
                  console.debug('[otel-client] Error parsing URL in fetch:', err);
                  span.setAttribute('error.url_parsing', true);

                  // Even with a parsing error, check if this might be a Next.js resource
                  // from the request URL directly
                  try {
                    let urlString = '';
                    if (typeof request === 'string') {
                      urlString = request;
                    } else if ('url' in request) {
                      urlString = request.url;
                    }

                    if (urlString) {
                      // Check for Next.js patterns even without proper URL parsing
                      if (urlString.includes('/_next/') ||
                          urlString.includes('.hot-update.json') ||
                          urlString.includes('.map')) {
                        span.setAttribute('telemetry.filtered', true);
                        span.setAttribute('telemetry.filtered_reason', 'next_static_resource');
                        return;
                      }

                      // Check if the URL should be traced, even in the error path
                      if (!shouldTraceUrl(urlString)) {
                        span.setAttribute('telemetry.filtered', true);
                        span.setAttribute('telemetry.filtered_reason', 'domain_not_traced_error_path');
                        return;
                      }
                    } else {
                      // If we can't determine URL, don't trace
                      span.setAttribute('telemetry.filtered', true);
                      span.setAttribute('telemetry.filtered_reason', 'url_unknown');
                      return;
                    }
                  } catch (nestedErr) {
                    // Ignore nested errors in URL string check but don't trace
                    console.debug('[otel-client] Error in fallback URL check:', nestedErr);
                    span.setAttribute('telemetry.filtered', true);
                    span.setAttribute('telemetry.filtered_reason', 'error_handling_failure');
                    return;
                  }
                }
              },
            }),
            xhrInstrumentation, // Add the instance we configured above
          );
        } else {
          console.debug('[otel-client] API request tracking disabled via configuration');
        }

        // Add optional instrumentations based on feature flags
        if (enableUserInteraction) {
          instrumentations.push(
            new UserInteractionInstrumentation({
              eventNames: ['click', 'submit'],
              shouldPreventSpanCreation: (eventName, element) => {
                // Prevent span creation for certain events
                if (eventName === 'toggle') {
                  return true;
                }

                // Filter out most click events except those on important elements
                if (eventName === 'click') {
                  // Only keep clicks on elements with data-track-click attribute or
                  // elements with important roles like buttons that perform actions
                  const target = element as HTMLElement;
                  const hasTrackAttribute = target.hasAttribute('data-track-click');
                  const isImportantElement =
                    target.tagName === 'BUTTON' ||
                    target.tagName === 'A' ||
                    target.hasAttribute('role') &&
                    ['button', 'link', 'menuitem', 'tab'].includes(target.getAttribute('role'));

                  const hasImportantParent =
                    target.closest('[data-track-click]') ||
                    target.closest('[role="button"]') ||
                    target.closest('[role="link"]') ||
                    target.closest('[role="menuitem"]') ||
                    target.closest('[role="tab"]');

                  // Only create spans for important interactions
                  return !(hasTrackAttribute || isImportantElement || hasImportantParent);
                }

                return false;
              },
            }),
          );
        }

        if (enableDocumentLoad) {
          instrumentations.push(
            new DocumentLoadInstrumentation({
              // Use the correct structure for applyCustomAttributesOnSpan
              applyCustomAttributesOnSpan: {
                documentLoad: span => {
                  // Add common attributes to document load spans
                  span.setAttribute('page.url', window.location.href);
                  span.setAttribute('page.referrer', document.referrer);
                  span.setAttribute('environment', environment);
                  span.setAttribute('device.type', deviceType);
                  span.setAttribute('device.os.name', osInfo.name);
                  span.setAttribute('device.os.version', osInfo.version);

                  // Set span status to OK
                  span.setStatus({code: SpanStatusCode.OK});
                },
              },
            }),
          );
        }

        // Register all instrumentations
        registerInstrumentations({
          instrumentations,
        });
        console.debug('[otel-client] Instrumentations registered');

        // Install session tracking
        initializeSessionTracking();

        // Initialize additional performance monitoring if enabled
        if (enablePerformanceTracking) {
          initializePerformanceMonitoring();
        }

        // Initialize error tracking if enabled
        if (enableErrorTracking) {
          initializeErrorTracking();
        }

        if (process.env.NODE_ENV === 'development') {
          console.debug('OpenTelemetry tracing initialized successfully');
        }
      } catch (initError) {
        console.error('[otel-client] Failed to initialize OpenTelemetry:', initError);
      }
    } catch (zoneError) {
      console.warn('[otel-client] Failed to load Zone.js context manager:', zoneError);
    }
  };

  // Start initialization process
  initWithZone();
}

/**
 * Force flush all pending spans to ensure they are sent to the collector
 * Call this function before navigation events or when unmounting critical components
 * @return {Promise<void>} Promise that resolves when flushing is complete
 */
export async function forceFlushTraces(): Promise<void> {
  if (tracerProvider) {
    try {
      console.debug('[otel-client] Force flushing pending spans...');
      await tracerProvider.forceFlush();
      console.debug('[otel-client] Spans flushed successfully');
    } catch (error) {
      console.warn('[otel-client] Error flushing spans:', error);
    }
  }
}

// Helper function to check if a span's status has been set
const checkSpanStatusSet = s => {
  // Simple check - if we can't directly check status
  // we can infer based on properties or just return false to be safe
  try {
    return s.status !== undefined || s._status !== undefined;
  } catch {
    return false;
  }
};

